import { type PropsWithChildren, memo, useMemo } from "react";

import type {
  Maybe,
  MoneyV2,
} from "@shopify/hydrogen-react/storefront-api-types";

import {
  CompareAtPriceDisplay,
  PriceContext,
} from "@components/Price/constants";
import { PriceView } from "@components/Price/Price.view";
import { type TextType } from "@components/Text";
import { type WithBreakpoints } from "@react/utilities";
import { formatMoney } from "@root/tablescape/utilities";
import { type TTailwindString } from "classnames";

type SizeValue = "3xl" | "lg" | "md" | "sm" | "xs" | "2xs" | "3xs";

type Props = {
  as?: TextType;
  className?: TTailwindString;
  compareAtPrice?: Maybe<MoneyV2>;
  compareAtPriceDisplay?: Maybe<CompareAtPriceDisplay>;
  context?: PriceContext;
  price: Partial<MoneyV2>;
  showCurrencySuffix?: boolean;
  size?: SizeValue | WithBreakpoints<SizeValue>;
};

const PriceBase = ({
  as = "body1",
  className,
  compareAtPrice,
  compareAtPriceDisplay = CompareAtPriceDisplay.STRIKETHROUGH,
  context,
  price,
  showCurrencySuffix = true,
  size,
}: PropsWithChildren<Props>) => {
  const currencySuffix = useMemo(() => {
    switch (window.Shopify?.currency?.active) {
      case "AUD":
      case "CAD": {
        return window.Shopify.currency.active;
      }

      default: {
        return undefined;
      }
    }
  }, []);

  const formattedPrice = useMemo(() => {
    if (!price?.amount || price.amount === "0.0") {
      return "Free";
    }

    return formatMoney(
      parseFloat(price.amount) * 100,
      // eslint-disable-next-line no-template-curly-in-string
      "${{ amount_no_trailing_zeroes }}",
      showCurrencySuffix ? currencySuffix : undefined
    );
  }, [price, currencySuffix, showCurrencySuffix]);

  const formattedCompareAtPrice = useMemo(() => {
    if (!compareAtPrice?.amount || compareAtPrice.amount === "0.0") {
      return undefined;
    }

    return formatMoney(
      parseFloat(compareAtPrice.amount) * 100,
      // eslint-disable-next-line no-template-curly-in-string
      "${{ amount_no_trailing_zeroes }}",
      showCurrencySuffix ? currencySuffix : undefined
    );
  }, [compareAtPrice, currencySuffix, showCurrencySuffix]);

  const isFree = useMemo(() => price?.amount === "0.0", [price?.amount]);

  const compareAtPriceSize = useMemo<
    SizeValue | WithBreakpoints<SizeValue> | undefined
  >(() => {
    if (
      context === PriceContext.PRODUCT_INTRO &&
      compareAtPriceDisplay === CompareAtPriceDisplay.VALUE
    ) {
      return { sm: "sm", md: "md" };
    } else if (
      context === PriceContext.PRODUCT_COMBO_OPTION &&
      compareAtPriceDisplay === CompareAtPriceDisplay.VALUE
    ) {
      return "xs";
    }
  }, [context, compareAtPriceDisplay]);

  return (
    <PriceView
      as={as}
      className={className}
      compareAtPrice={formattedCompareAtPrice}
      compareAtPriceDisplay={compareAtPriceDisplay}
      compareAtPriceSize={compareAtPriceSize}
      context={context}
      isFree={isFree}
      price={formattedPrice}
      size={size}
    />
  );
};

export const Price = memo(PriceBase);
